import { useEffect, useState, useRef } from 'react'
import { observer, inject } from 'mobx-react'
import { toJS } from 'mobx'
import dayjs from 'dayjs'

import { FieldContainer, Title, BulletText, CircleBulletText } from '../styled'

// Module-level flag to prevent multiple initializations across component remounts
let hasGloballyInitialized = false

const UpdateInformation = props => {
  const {
    store: { accounts, person, spouse, allPensions, goals },
    getInvestments,
    setInfoNeedsUpdating,
  } = props

  const [showGoalWithdrawal, setShowGoalWithdrawal] = useState(false)
  const [showLumpSum, setShowLumpSum] = useState(false)
  const [showOutdated, setShowOutdated] = useState(false)
  const [outdatedAccounts, setOutdatedAccounts] = useState([])
  const checksCompleted = useRef(false)

  useEffect(() => {
    if (!checksCompleted.current) return

    if (!showGoalWithdrawal && !showLumpSum && !showOutdated) {
      setInfoNeedsUpdating(false)
      return
    }
    setInfoNeedsUpdating(true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showGoalWithdrawal, showLumpSum, showOutdated])

  useEffect(() => {
    if (hasGloballyInitialized) return
    hasGloballyInitialized = true

    // Convert MobX observables to plain JS to avoid maximum update depth exceeded
    const plainAccounts = toJS(accounts)
    const plainGoals = toJS(goals)
    const plainPensions = toJS(allPensions)
    const plainPerson = toJS(person)
    const plainSpouse = toJS(spouse)

    const currentYearStartOfDate = dayjs().startOf('year').format('YYYY-MM-DD')
    const shortListAccounts = plainAccounts.filter(
      account => account.type === 'Institutional' || account.type === 'NonGc'
    )

    const getAllInvestments = async () => {
      const newOutdatedAccounts = []

      for (const account of shortListAccounts) {
        const id = account.id
        let type = account.type

        switch (type) {
          case 'Institutional':
            type = 'institutional'
            break
          case 'NonGc':
            type = 'non-gc'
            break
          default:
            break
        }
        const accountInv = await getInvestments({ type, id })

        accountInv.every(investment => {
          if (parseInt(dayjs(investment.dateOfUserInfo).toNow(true)) >= 185) {
            newOutdatedAccounts.push(account)
            return false
          } else {
            return true
          }
        })
      }

      if (newOutdatedAccounts.length > 0) {
        setOutdatedAccounts(newOutdatedAccounts)
        setShowOutdated(true)
      }
    }

    const checkGoalWithdraw = () => {
      plainGoals?.every(goal => {
        if (
          goal.disbursements.map(disbursement => {
            if (disbursement.startDate === currentYearStartOfDate) {
              if (!disbursement.paid) {
                return true
              } else {
                return false
              }
            } else {
              return false
            }
          })
        ) {
          setShowGoalWithdrawal(true)
          return false
        } else {
          return true
        }
      })
    }

    const checkLump = () => {
      const pensions = plainPensions
        .filter(account => account.paymentType === 'L')
        .filter(account => account.lumpsumreceived === 'N')

      if (plainPerson.includeSpouse) {
        const spousePensions = pensions
          .filter(account => account.ownerId === plainSpouse.id)
          .filter(account => account.pensionStartAge <= plainSpouse.age)
        const primaryPensions = pensions
          .filter(account => account.ownerId === plainPerson.id)
          .filter(account => account.pensionStartAge <= plainPerson.age)

        if (spousePensions.length > 0 || primaryPensions.length > 0) {
          setShowLumpSum(true)
        }
      } else {
        const primaryPensions = pensions.filter(
          account => account.pensionStartAge === plainPerson.age
        )

        if (primaryPensions.length > 0) {
          setShowLumpSum(true)
        }
      }
    }

    try {
      getAllInvestments()
      checkGoalWithdraw()
      checkLump()

      checksCompleted.current = true
    } catch (err) {
      console.log(err)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const showOutdate = () => {
    return (
      <>
        <BulletText>
          Outdated investment balances—locate your most recent statement or log into each of the
          account below to obtain the latest balances, then log in and let us know
        </BulletText>
        {outdatedAccounts.map(acct => {
          return (
            <CircleBulletText key={acct.id}>
              {acct.name} (
              {acct.participantId === person.id
                ? person.preferredName
                  ? person.preferredName
                  : person.firstName
                : spouse.firstName}
              )
            </CircleBulletText>
          )
        })}
      </>
    )
  }

  if (!showGoalWithdrawal && !showLumpSum && !showOutdated) return null
  return (
    <>
      <FieldContainer>
        {(showGoalWithdrawal || showLumpSum || showOutdated) && (
          <Title>Update information to clear alerts and to be more certain of results</Title>
        )}
        {showGoalWithdrawal && (
          <BulletText>
            Goal withdrawal—when you've withdrawn this year's amount, log in and let us know
          </BulletText>
        )}
        {showLumpSum && (
          <BulletText>
            Lump sum pension payout—when you've received the payout and have deposited it into a
            tax-advantaged account, log in and let us know
          </BulletText>
        )}
        {showOutdated && showOutdate()}
      </FieldContainer>
    </>
  )
}

export default inject('store')(observer(UpdateInformation))
